import {
  RESTAURANT_BENCHMARKS,
  compareToBenchmarks,
  getBenchmarkSummary,
  getPercentileColor,
  BenchmarkMetric,
} from '@/lib/benchmarks';
import { Trophy, TrendingUp, Target, Award } from 'lucide-react';

interface BenchmarkComparisonProps {
  metrics: {
    performance: number;
    seo?: number;
    accessibility: number;
    bestPractices: number;
    lcp: number;
    fcp: number;
    cls: number;
    ttfb: number;
  };
}

export function BenchmarkComparison({ metrics }: BenchmarkComparisonProps) {
  const comparison = compareToBenchmarks(metrics);
  const summary = getBenchmarkSummary({
    performance: metrics.performance,
    seo: metrics.seo ?? 0,
    accessibility: metrics.accessibility,
    bestPractices: metrics.bestPractices,
  });

  const getRatingIcon = () => {
    switch (summary.overallRating) {
      case 'excellent':
        return <Trophy className="h-6 w-6 text-green-500/70" />;
      case 'good':
        return <Award className="h-6 w-6 text-green-500/70" />;
      case 'average':
        return <Target className="h-6 w-6 text-orange-400/70" />;
      default:
        return <TrendingUp className="h-6 w-6 text-orange-500/70" />;
    }
  };

  const getRatingLabel = () => {
    switch (summary.overallRating) {
      case 'excellent':
        return 'Excellent - Top 10%';
      case 'good':
        return 'Good - Top 25%';
      case 'average':
        return 'Average';
      case 'below-average':
        return 'Below Average';
      case 'poor':
        return 'Needs Improvement';
    }
  };

  return (
    <div className="space-y-6">
      {/* Summary Card */}
      <div className="bg-secondary/30 rounded-lg p-6">
        <div className="flex items-center gap-4 mb-4">
          {getRatingIcon()}
          <div>
            <h3 className="text-lg font-medium">Industry Comparison</h3>
            <p className="text-sm text-muted-foreground">
              Compared to other restaurant websites
            </p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mt-4">
          <div className="text-center">
            <p className="text-3xl font-light">{summary.averagePercentile}</p>
            <p className="text-xs text-muted-foreground">Avg Percentile</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-light">{summary.metricsAboveMedian}/3</p>
            <p className="text-xs text-muted-foreground">Above Median</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-light">{summary.metricsInTop25}/3</p>
            <p className="text-xs text-muted-foreground">Top 25%</p>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-border/50 text-center">
          <span className={`text-sm font-medium ${getPercentileColor(summary.averagePercentile)}`}>
            {getRatingLabel()}
          </span>
        </div>
      </div>

      {/* Detailed Comparison */}
      <div className="space-y-4">
        <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
          Score Benchmarks
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <BenchmarkRow
            metric="performance"
            label="Performance"
            value={metrics.performance}
            comparison={comparison.performance}
          />
          <BenchmarkRow
            metric="accessibility"
            label="Accessibility"
            value={metrics.accessibility}
            comparison={comparison.accessibility}
          />
          <BenchmarkRow
            metric="bestPractices"
            label="Best Practices"
            value={metrics.bestPractices}
            comparison={comparison.bestPractices}
          />
        </div>
      </div>

      {/* Vitals Comparison */}
      <div className="space-y-4">
        <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
          Core Web Vitals Benchmarks
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <BenchmarkRow
            metric="lcp"
            label="LCP"
            value={metrics.lcp}
            comparison={comparison.lcp}
            unit="s"
          />
          <BenchmarkRow
            metric="fcp"
            label="FCP"
            value={metrics.fcp}
            comparison={comparison.fcp}
            unit="s"
          />
          <BenchmarkRow
            metric="cls"
            label="CLS"
            value={metrics.cls}
            comparison={comparison.cls}
          />
          <BenchmarkRow
            metric="ttfb"
            label="TTFB"
            value={metrics.ttfb}
            comparison={comparison.ttfb}
            unit="s"
          />
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
        <span className="flex items-center">
          <span className="inline-block w-2 h-2 rounded-full bg-green-500/60 mr-1.5"></span>
          Top 25%
        </span>
        <span className="flex items-center">
          <span className="inline-block w-2 h-2 rounded-full bg-orange-400/60 mr-1.5"></span>
          Above Average
        </span>
        <span className="flex items-center">
          <span className="inline-block w-2 h-2 rounded-full bg-orange-500/60 mr-1.5"></span>
          Below Average
        </span>
        <span className="flex items-center">
          <span className="inline-block w-2 h-2 rounded-full bg-red-500/60 mr-1.5"></span>
          Bottom 25%
        </span>
      </div>
    </div>
  );
}

function BenchmarkRow({
  metric,
  label,
  value,
  comparison,
  unit = '',
}: {
  metric: BenchmarkMetric;
  label: string;
  value: number;
  comparison: { percentile: number; description: string; isAboveTarget: boolean };
  unit?: string;
}) {
  const benchmark = RESTAURANT_BENCHMARKS[metric];
  const isLowerBetter = benchmark.betterDirection === 'lower';

  // Calculate position on the bar (0-100%)
  const { p25, p50, p75, p90 } = benchmark.percentiles;
  let position: number;

  if (isLowerBetter) {
    // For metrics where lower is better, invert the scale
    if (value >= p25) position = 10;
    else if (value >= p50) position = 25 + ((p25 - value) / (p25 - p50)) * 25;
    else if (value >= p75) position = 50 + ((p50 - value) / (p50 - p75)) * 25;
    else if (value >= p90) position = 75 + ((p75 - value) / (p75 - p90)) * 15;
    else position = 95;
  } else {
    // For metrics where higher is better
    if (value <= p25) position = 10;
    else if (value <= p50) position = 25 + ((value - p25) / (p50 - p25)) * 25;
    else if (value <= p75) position = 50 + ((value - p50) / (p75 - p50)) * 25;
    else if (value <= p90) position = 75 + ((value - p75) / (p90 - p75)) * 15;
    else position = 95;
  }

  position = Math.max(5, Math.min(95, position));

  return (
    <div className="bg-secondary/20 rounded-lg p-4">
      <div className="flex justify-between items-start mb-2">
        <span className="text-sm font-medium">{label}</span>
        <span className={`text-sm ${getPercentileColor(comparison.percentile)}`}>
          {comparison.description}
        </span>
      </div>

      {/* Value */}
      <div className="flex items-baseline gap-1 mb-3">
        <span className="text-2xl font-light">
          {typeof value === 'number' ? (unit ? value.toFixed(2) : value) : value}
        </span>
        {unit && <span className="text-xs text-muted-foreground">{unit}</span>}
      </div>

      {/* Benchmark Bar */}
      <div className="relative h-2 bg-secondary rounded-full overflow-hidden">
        {/* Gradient background - muted colors for subtlety */}
        <div
          className="absolute inset-0 opacity-50"
          style={{
            background: 'linear-gradient(to right, #ef4444 0%, #f97316 25%, #fb923c 50%, #22c55e 75%, #22c55e 100%)',
          }}
        />

        {/* Position marker */}
        <div
          className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full border-2 border-background shadow"
          style={{ left: `${position}%`, transform: 'translateX(-50%) translateY(-50%)' }}
        />
      </div>

      {/* Scale labels */}
      <div className="flex justify-between text-xs text-muted-foreground mt-1">
        <span>p25</span>
        <span>p50</span>
        <span>p75</span>
        <span>p90</span>
      </div>
    </div>
  );
}
